import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";

import type { Ta } from "#scripts/components/ui/icons.tsx";
import { cn } from "#scripts/lib/utils.ts";

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center whitespace-nowrap user-select-none",
    "text-xs font-medium font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "disabled:pointer-events-none disabled:opacity-50",
    "border border-transparent",
  ],
  {
    variants: {
      variant: {
        primary: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border-input bg-background hover:bg-bg-elevated hover:text-text",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        selected: "border-border bg-bg-elevated text-text",
        ghost: "hover:bg-border/30 hover:text-text",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        xs: "h-6 px-1 gap-1",
        sm: "h-7 px-3 gap-1",
        default: "h-8 px-4 gap-1.5",
        lg: "h-10 px-5 gap-2",
        icon: "h-8 w-8",
      },
      rounded: {
        default: "rounded-full",
        rounded: "rounded-full",
        squarish: "rounded-md",
        groupEnd: "rounded-full border-l-0 rounded-l-none pl-3",
        groupStart: "rounded-full rounded-r-none pr-3",
        groupMiddle: "rounded-none border-l-0 border-r",
      },
    },
    defaultVariants: {
      variant: "outline",
      size: "default",
      rounded: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  IconStart?: Ta.TablerIcon;
  IconEnd?: Ta.TablerIcon;
}

const iconStartClassForSize = {
  xs: "w-3 h-3 -ml-0.5",
  sm: "-ml-1",
  default: "-ml-1.5",
  lg: "-ml-2",
  icon: "w-5 h-5",
} as const;

const iconEndClassForSize = {
  xs: "w-3 h-3 -mr-0.5",
  sm: "-mr-1",
  default: "-mr-1.5",
  lg: "-mr-2",
  icon: "w-5 h-5",
} as const;

/**
 * @example
 * <Button data-loc="phosphor-blog/scripts/components/ui/button.tsx:76:12" data-name="Button" className="w-full" size="sm" IconStart={Ta.IconPlus}>Add</Button>
 * <Button data-loc="phosphor-blog/scripts/components/ui/button.tsx:77:12" data-name="Button" className="w-full" size="sm" IconStart={Ta.IconPlus} IconEnd={Ta.IconChevronDown}>Add</Button>
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, rounded, asChild = false, IconStart, IconEnd, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp className={cn(buttonVariants({ variant, size, rounded, className }))} ref={ref} {...props}>
        {IconStart && (
          <IconStart
            aria-hidden="true"
            data-loc="phosphor-blog/scripts/components/ui/button.tsx:87:13" data-name="IconStart" className={cn(
              "w-4 h-4 stroke-[1.25]",
              iconStartClassForSize[size ?? "default"],
              !IconEnd && !children && iconEndClassForSize[size ?? "default"],
            )}
          />
        )}
        {children}
        {IconEnd && (
          <IconEnd
            aria-hidden="true"
            data-loc="phosphor-blog/scripts/components/ui/button.tsx:98:13" data-name="IconEnd" className={cn(
              "w-4 h-4 stroke-[1.25]",
              iconEndClassForSize[size ?? "default"],
              !IconStart && !children && iconStartClassForSize[size ?? "default"],
            )}
          />
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
